// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

export type ActivityItemKind =
  | 'user'
  | 'agent'
  | 'tool'
  | 'progress'
  | 'question'
  | 'result'
  | 'system';

export type ActivityPresentation = {
  kind: ActivityItemKind;
  title: string;
  detail: string;
};

type ActivityDefinition = {
  kind: ActivityItemKind;
  title: string;
};

const LEGACY_ACTIVITY: Record<string, ActivityDefinition> = {
  confirmed: { kind: 'user', title: 'You asked' },
  human_reply: { kind: 'user', title: 'You replied' },
  activate_agent: { kind: 'agent', title: 'Agent started' },
  create_agent: { kind: 'agent', title: 'Agent created' },
  deactivate_agent: { kind: 'agent', title: 'Agent finished' },
  activate_toolkit: { kind: 'tool', title: 'Tool started' },
  deactivate_toolkit: { kind: 'tool', title: 'Tool finished' },
  tool_started: { kind: 'tool', title: 'Tool started' },
  tool_finished: { kind: 'tool', title: 'Tool finished' },
  notice: { kind: 'progress', title: 'Progress update' },
  ask: { kind: 'question', title: 'Eigent needs your input' },
  end: { kind: 'result', title: 'Run completed' },
  wait_confirm: { kind: 'result', title: 'Response ready' },
  error: { kind: 'result', title: 'Run failed' },
  failed: { kind: 'result', title: 'Run failed' },
  budget_not_enough: { kind: 'result', title: 'Budget unavailable' },
  context_too_long: { kind: 'result', title: 'Context limit reached' },
};

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

export function extractActivityText(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  const data = asRecord(value);
  for (const key of [
    'question',
    'content',
    'message',
    'reply',
    'notice',
    'answer',
    'summary',
    'description',
    'agent',
    'name',
    'toolkit_name',
    'method_name',
  ]) {
    const item = data[key];
    if (typeof item === 'string' && item.trim()) return item.trim();
  }
  return '';
}

export function humanizeActivityType(value: string): string {
  return value
    .replace(/[._-]+/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

export function presentLegacyActivity(
  step: string,
  data: unknown
): ActivityPresentation {
  const definition = LEGACY_ACTIVITY[step];
  return {
    kind: definition?.kind || 'system',
    title: definition?.title || humanizeActivityType(step),
    detail: extractActivityText(data),
  };
}
